import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Search, Plus, PawPrint, Trash2, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function Patients() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trash, setTrash] = useState(false);

  const load = async (query = "", showTrash = trash) => {
    setLoading(true);
    try {
      const params = {};
      if (query) params.q = query;
      if (showTrash) params.deleted = true;
      const { data } = await api.get("/patients", { params });
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load("", false); }, []);

  const onSearch = (e) => { e.preventDefault(); load(q); };

  const toggleTrash = () => {
    const next = !trash;
    setTrash(next);
    setQ("");
    load("", next);
  };

  const restore = async (e, p) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.post(`/patients/${p.id}/restore`);
      toast.success(`${p.pet_name} restored`);
      load("", true);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Restore failed");
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1400px] mx-auto animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">{trash ? "Trash" : "Patients"}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {trash ? "Deleted profiles are auto-removed 7 days after deletion. Restore any before then."
                   : "Search by pet name, owner, phone, case #, breed, or microchip."}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button variant={trash ? "default" : "outline"} onClick={toggleTrash} data-testid="toggle-trash-btn">
              <Trash2 className="h-4 w-4 mr-2" />{trash ? "Back to Patients" : "Trash"}
            </Button>
          )}
          {!trash && (
            <Link to="/patients/new">
              <Button data-testid="new-patient-btn"><Plus className="h-4 w-4 mr-2" />New Patient</Button>
            </Link>
          )}
        </div>
      </div>

      {!trash && (
        <form onSubmit={onSearch} className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="patient-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                   placeholder="Search patients…" className="pl-9" />
          </div>
          <Button type="submit" variant="outline">Search</Button>
        </form>
      )}

      <Card className="p-0 border-border/60 overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 text-xs uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
          <div className="col-span-4">Pet</div>
          <div className="col-span-3">Owner</div>
          <div className="col-span-2">Phone</div>
          <div className="col-span-2">Case #</div>
          <div className="col-span-1 text-right">{trash ? "Action" : "Date"}</div>
        </div>
        {loading && <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>}
        {!loading && items.length === 0 && (
          <div className="p-12 text-center text-muted-foreground">
            <PawPrint className="h-10 w-10 mx-auto mb-3 opacity-40" />
            {trash ? "Trash is empty." : <>No patients found. <Link to="/patients/new" className="text-foreground underline">Add a new patient</Link>.</>}
          </div>
        )}
        <div className="divide-y divide-border">
          {items.map((p) => {
            const row = (
              <>
                <div className="col-span-4 min-w-0 flex items-center gap-3">
                  {p.photo_file_id ? (
                    <img src={fileUrl(p.photo_file_id)} alt={p.pet_name} className="w-9 h-9 rounded-lg object-cover border border-border shrink-0" />
                  ) : (
                    <div className="w-9 h-9 rounded-lg bg-muted border border-border grid place-items-center shrink-0">
                      <PawPrint className="h-4 w-4 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{p.pet_name}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.species}{p.breed ? ` · ${p.breed}` : ""}</div>
                  </div>
                </div>
                <div className="col-span-3 truncate text-sm">{p.owner_name}</div>
                <div className="col-span-2 text-sm tabular-nums">{p.phone}</div>
                <div className="col-span-2 text-sm font-mono">{p.case_number}</div>
                <div className="col-span-1 text-right text-xs text-muted-foreground">
                  {trash
                    ? <Button size="sm" variant="outline" data-testid={`restore-patient-${p.id}`} onClick={(e) => restore(e, p)}><RotateCcw className="h-3.5 w-3.5 mr-1" />Restore</Button>
                    : new Date(p.created_at).toLocaleDateString()}
                </div>
              </>
            );
            return trash ? (
              <div key={p.id} data-testid={`trash-row-${p.id}`} className="grid grid-cols-12 px-5 py-4 items-center">
                {row}
              </div>
            ) : (
              <Link key={p.id} to={`/patients/${p.id}`} data-testid={`patient-row-${p.id}`}
                    className="grid grid-cols-12 px-5 py-4 items-center hover:bg-muted/40 transition-colors">
                {row}
              </Link>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
